<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { baseCodeApi } from '@/shared/api/baseCodeApi'
import { useDatePicker, formatDateForInput } from '@/shared/composables/useDatePicker';
import { useMyNricDateOfBirthAutoFill } from '@/shared/composables/useMyNricDateOfBirthAutoFill';
import type { CreateIndividualData, Contact } from '../types/contact.types';
import SyncFieldIndicator from '@features/Entity_my/components/SyncFieldIndicator.vue';
import {
    resolveContactDetailFieldReadonly,
    resolveContactDetailFormReadonly,
} from '../utils/contactMyDetailEditPolicy';
import {
    isMyPassportIdType,
    MY_PASSPORT_ID_TYPE_CD,
    resolveContactIdTypeCd,
} from '../utils/myPassportIdType';
import type { EntityOfficerContactOverlay, EntityBeneficialOwnerContactOverlay } from '@features/Entity_my/types/entity.types';
import {
    MY_LICENSE_QUALIFICATION_PREFIXES,
    mergeMySecretaryLicense,
    splitMySecretaryLicense,
    type MyQualificationPrefix,
} from '@/features/register/utils/mySecretaryLicense';

interface SyncFieldChangeLite {
    type: 'UPDATE' | 'CONFLICT';
    before: unknown;
    after: unknown;
    note?: string;
}

interface Props {
    modelValue?: CreateIndividualData | Contact;
    readonly?: boolean;
    hideKycStatus?: boolean;
    readonlyFields?: string[];
    detailMode?: boolean;
    /**
     * 싱크 변경 맵 (필드명 → 변경정보). Entity(officer) 컨텍스트에서만 전달되며,
     * 전달되면 해당 필드 우측에 변경/conflict 인디케이터 표시. Contact 메뉴에서는 미전달=표시 안 함.
     */
    syncChanges?: Map<string, SyncFieldChangeLite> | null;
    /** Entity Officer 모달 — L_OFFICER 직책별 표시 필드 (글로벌 프로필과 분리) */
    entityOfficerOverlay?: EntityOfficerContactOverlay | null;
    /** Entity Beneficial Owner 모달 — L_BENEFICIALOWNER.OCCUPATION */
    entityBeneficialOwnerOverlay?: EntityBeneficialOwnerContactOverlay | null;
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false,
    hideKycStatus: false,
    readonlyFields: () => [],
    detailMode: false,
    syncChanges: null,
    entityOfficerOverlay: null,
    entityBeneficialOwnerOverlay: null,
});

/** 싱크 변경 조회 (여러 필드명 중 첫 매칭) */
function syncChange(...fields: string[]): SyncFieldChangeLite | null {
    if (!props.syncChanges) return null;
    for (const f of fields) {
        const hit = props.syncChanges.get(f);
        if (hit) return hit;
    }
    return null;
}

const emit = defineEmits<{
    'update:modelValue': [value: CreateIndividualData | Contact];
    'update:entityOfficerOverlay': [value: EntityOfficerContactOverlay];
    'update:entityBeneficialOwnerOverlay': [value: EntityBeneficialOwnerContactOverlay];
}>();

const { createSyncT } = useTranslation();
const languageStore = useLanguageStore();
const sessionStore = useSessionStore();
// 반응형 번역 함수를 computed로 생성
const t = computed(() => {
    const translate = createSyncT(languageStore.currentLanguage);
    return translate;
});

// 세션의 JURISDICTION이 MY인지 확인
const isJurisdictionMY = computed(() => {
    const customerInfo = sessionStore.user?.customer || null;
    const jurisdiction = customerInfo?.jurisdiction || '';
    const isMY = jurisdiction?.toUpperCase() === 'MY' || jurisdiction?.toUpperCase() === 'MALAYSIA';
    return isMY;
});

const showMyProfileFields = computed(() => isJurisdictionMY.value);

const showEntityOfficerDirectorFields = computed(
    () => props.entityOfficerOverlay?.positionGroup === 'director',
);
const showEntityOfficerSecretaryFields = computed(
    () => props.entityOfficerOverlay?.positionGroup === 'secretary',
);
const showEntityOfficerManagerFields = computed(
    () => props.entityOfficerOverlay?.positionGroup === 'manager',
);
const showEntityBeneficialOwnerFields = computed(
    () => !!props.entityBeneficialOwnerOverlay,
);

/** MY 글로벌 컨텍트: ID TYPE = M01005(여권)일 때만 Issuing Country / Passport Expiry 표시 */
const showMyPassportFields = computed(() => {
    if (!isJurisdictionMY.value) return false;
    return isMyPassportIdType(props.modelValue);
});

/** Passport Expiry: MY는 여권 ID TYPE일 때만, 그 외 jurisdiction은 항상 표시 */
const showPassportExpiryField = computed(() => {
    if (!isJurisdictionMY.value) return true;
    return showMyPassportFields.value;
});

const myIdRowColSpan = computed(() => 6);

/** MY Registrable Individual: 단일 Name 필드 */
const showMySingleNameField = computed(() => isJurisdictionMY.value && !props.detailMode);

/** MY: First / Middle / Last 숨김 (Create·Detail) */
const hideSplitNameFields = computed(() => isJurisdictionMY.value);

const isJurisdictionSG = computed(() => {
    const customerInfo = sessionStore.user?.customer || null;
    return customerInfo?.jurisdiction === 'SG';
});

/** MY Contact Detail: 이름만 readonly, 나머지 필드 편집 가능 */
const isMyDetailEditableExceptName = computed(
    () => isJurisdictionMY.value && props.detailMode,
);

const effectiveFormReadonly = computed(() =>
    resolveContactDetailFormReadonly(isMyDetailEditableExceptName.value, props.readonly),
);

const isBizFileRegistrant = computed(() => {
    const value = props.modelValue || ({} as any);
    const registerType = String(value.CONTACT_REGISTER_TYPE || value.contactRegisterType || '')
        .trim()
        .toUpperCase();
    return registerType === 'A06001';
});

const isReadonlyField = (fieldName: string) => {
    if (
        props.detailMode &&
        isJurisdictionSG.value &&
        isBizFileRegistrant.value &&
        fieldName === 'PASSPORT_NO_EXPIRY_DATE'
    ) {
        return false;
    }
    return resolveContactDetailFieldReadonly(fieldName, {
        isMyDetailEditableExceptName: isMyDetailEditableExceptName.value,
        isBizFileRegistrant: isBizFileRegistrant.value,
        propsReadonly: props.readonly,
        readonlyFields: props.readonlyFields,
    });
};

function patchEntityOfficerOverlay(patch: Partial<EntityOfficerContactOverlay>) {
    if (!props.entityOfficerOverlay) return;
    emit('update:entityOfficerOverlay', {
        ...props.entityOfficerOverlay,
        ...patch,
    });
}

function patchEntityBeneficialOwnerOverlay(patch: Partial<EntityBeneficialOwnerContactOverlay>) {
    if (!props.entityBeneficialOwnerOverlay) return;
    emit('update:entityBeneficialOwnerOverlay', {
        ...props.entityBeneficialOwnerOverlay,
        ...patch,
    });
}

const entityOfficerQualificationPrefix = ref<MyQualificationPrefix>('');
const entityOfficerLicenseDigits = ref('');
const entityOfficerQualificationItems = [...MY_LICENSE_QUALIFICATION_PREFIXES];

watch(
    () => props.entityOfficerOverlay?.secretaryLicenseNo,
    (lic) => {
        const { prefix, digits } = splitMySecretaryLicense(String(lic ?? ''));
        entityOfficerQualificationPrefix.value = prefix;
        entityOfficerLicenseDigits.value = digits;
    },
    { immediate: true },
);

function patchEntityOfficerLicenseFromParts(prefix: string, digits: string) {
    if (effectiveFormReadonly.value) return;
    patchEntityOfficerOverlay({
        secretaryLicenseNo: mergeMySecretaryLicense(prefix, digits),
    });
}

function onEntityOfficerQualificationChange(val: unknown) {
    if (effectiveFormReadonly.value) return;
    const next = String(val ?? '').trim().toUpperCase();
    entityOfficerQualificationPrefix.value = MY_LICENSE_QUALIFICATION_PREFIXES.includes(
        next as (typeof MY_LICENSE_QUALIFICATION_PREFIXES)[number],
    )
        ? (next as (typeof MY_LICENSE_QUALIFICATION_PREFIXES)[number])
        : '';
    patchEntityOfficerLicenseFromParts(
        entityOfficerQualificationPrefix.value,
        entityOfficerLicenseDigits.value,
    );
}

function onEntityOfficerLicenseDigitsChange(val: unknown) {
    if (effectiveFormReadonly.value) return;
    entityOfficerLicenseDigits.value = String(val ?? '').replace(/\D/g, '');
    patchEntityOfficerLicenseFromParts(
        entityOfficerQualificationPrefix.value,
        entityOfficerLicenseDigits.value,
    );
}

const genderOptions = ['male', 'female'];
const raceOptions = [
    'Bidayuh',
    'Chinese',
    'Dusun',
    'Foreigner',
    'Iban',
    'Indian',
    'Kadazan',
    'Malay',
    'Other Race',
];

// snake_case 필드로 변환하여 사용
const basicData = computed({
    get: () => {
        const value = props.modelValue || {} as any;
        // snake_case 필드 우선 사용, 없으면 camelCase 사용, Create 페이지의 필드명도 지원
        const middleName = value.MIDDLE_NAME || value.middleName || '';
        const rawNat = value.NATIONALITY_CD ?? value.nationalityCd ?? value.nationality;
        const nationalityNormalized =
            rawNat == null || String(rawNat).trim() === '' ? null : String(rawNat);

        return {
            ...value,
            ID_TYPE_CD: value.ID_TYPE_CD || value.idTypeCd || value.identificationType || '',
            CONTACT_REG_NO: value.CONTACT_REG_NO || value.contactRegNo || value.identificationNo || '',
            NATIONALITY_CD: nationalityNormalized,
            DATE_OF_BIRTH: value.DATE_OF_BIRTH || value.dateOfBirth || '',
            PASSPORT_NO_EXPIRY_DATE: value.PASSPORT_NO_EXPIRY_DATE || value.passportNoExpiryDate || '',
            PROFILE_GENDER: value.PROFILE_GENDER || value.profileGender || value.gender || '',
            PROFILE_RACE: value.PROFILE_RACE || value.profileRace || value.race || '',
            PROFILE_OCCUPATION: value.PROFILE_OCCUPATION || value.profileOccupation || value.Occupation || value.occupation || '',
            PROFILE_SECRETARY_LICENSE_NO: value.PROFILE_SECRETARY_LICENSE_NO || value.profileSecretaryLicenseNo || value.license_number || '',
            PROFILE_SECRETARY_CERTIFICATE_NO:
                value.PROFILE_SECRETARY_CERTIFICATE_NO ||
                value.profileSecretaryCertificateNo ||
                (value as any).SecretaryCertificateNo ||
                '',
            PROFILE_OTHER_DIRECTORSHIP:
                value.PROFILE_OTHER_DIRECTORSHIP ||
                value.profileOtherDirectorship ||
                (value as any).OtherDirectorship ||
                '',
            PROFILE_FIRM_NO:
                value.PROFILE_FIRM_NO || value.profileFirmNo || (value as any).FirmNo || '',
            PROFILE_ISSUING_COUNTRY:
                value.PROFILE_ISSUING_COUNTRY || value.profileIssuingCountry || '',
            PROFILE_PLACE_OF_BIRTH:
                value.PROFILE_PLACE_OF_BIRTH || value.profilePlaceOfBirth || '',
            PROFILE_IS_MEETING:
                value.PROFILE_IS_MEETING ?? value.profileIsMeeting ?? null,
            CONTACT_NAME: value.CONTACT_NAME || value.contactName || '',
            FIRST_NAME: value.FIRST_NAME || value.firstName || '',
            // Middle Name이 비어있으면 공백 문자 하나로 설정하여 라벨이 항상 위에 표시되도록 함
            MIDDLE_NAME: middleName || ' ',
            SURNAME: value.SURNAME || value.surname || '',
        };
    },
    set: (value) => {
        // snake_case로 변환하여 emit
        // Middle Name의 공백 문자는 빈 문자열로 변환
        const updated = {
            ...props.modelValue,
            ID_TYPE_CD: value.ID_TYPE_CD,
            idTypeCd: value.ID_TYPE_CD,
            identificationType: value.ID_TYPE_CD, // Create 페이지용
            CONTACT_REG_NO: value.CONTACT_REG_NO,
            contactRegNo: value.CONTACT_REG_NO,
            identificationNo: value.CONTACT_REG_NO, // Create 페이지용
            ...(() => {
                const rawN = value.NATIONALITY_CD;
                const nNorm =
                    rawN == null || String(rawN).trim() === '' ? null : String(rawN);
                return {
                    NATIONALITY_CD: nNorm,
                    nationalityCd: nNorm,
                    nationality: nNorm,
                };
            })(),
            DATE_OF_BIRTH: value.DATE_OF_BIRTH,
            dateOfBirth: value.DATE_OF_BIRTH,
            PASSPORT_NO_EXPIRY_DATE: value.PASSPORT_NO_EXPIRY_DATE,
            passportNoExpiryDate: value.PASSPORT_NO_EXPIRY_DATE,
            PROFILE_GENDER: value.PROFILE_GENDER,
            profileGender: value.PROFILE_GENDER,
            PROFILE_RACE: value.PROFILE_RACE,
            profileRace: value.PROFILE_RACE,
            PROFILE_OCCUPATION: value.PROFILE_OCCUPATION,
            profileOccupation: value.PROFILE_OCCUPATION,
            PROFILE_SECRETARY_LICENSE_NO: value.PROFILE_SECRETARY_LICENSE_NO,
            profileSecretaryLicenseNo: value.PROFILE_SECRETARY_LICENSE_NO,
            PROFILE_SECRETARY_CERTIFICATE_NO: value.PROFILE_SECRETARY_CERTIFICATE_NO,
            profileSecretaryCertificateNo: value.PROFILE_SECRETARY_CERTIFICATE_NO,
            PROFILE_OTHER_DIRECTORSHIP: value.PROFILE_OTHER_DIRECTORSHIP,
            profileOtherDirectorship: value.PROFILE_OTHER_DIRECTORSHIP,
            PROFILE_FIRM_NO: value.PROFILE_FIRM_NO,
            profileFirmNo: value.PROFILE_FIRM_NO,
            PROFILE_ISSUING_COUNTRY: value.PROFILE_ISSUING_COUNTRY,
            profileIssuingCountry: value.PROFILE_ISSUING_COUNTRY,
            PROFILE_PLACE_OF_BIRTH: value.PROFILE_PLACE_OF_BIRTH,
            profilePlaceOfBirth: value.PROFILE_PLACE_OF_BIRTH,
            PROFILE_IS_MEETING: value.PROFILE_IS_MEETING,
            profileIsMeeting: value.PROFILE_IS_MEETING,
            CONTACT_NAME: value.CONTACT_NAME,
            contactName: value.CONTACT_NAME,
            FIRST_NAME: value.FIRST_NAME,
            firstName: value.FIRST_NAME,
            MIDDLE_NAME: value.MIDDLE_NAME?.trim() || '',
            middleName: value.MIDDLE_NAME?.trim() || '',
            SURNAME: value.SURNAME,
            surname: value.SURNAME,
        } as CreateIndividualData | Contact;
        emit('update:modelValue', updated);
    }
});

// Date of Birth - useDatePicker 사용 (basicData 이후에 정의)
const { dateValue: dateOfBirth, displayValue: dateOfBirthDisplay, menuOpen: dateOfBirthMenu, calendarDate: dateOfBirthCalendarDate } = useDatePicker(
    computed(() => {
        const value = props.modelValue || {} as any;
        return value.DATE_OF_BIRTH || value.dateOfBirth || '';
    }),
    true // 달력 열 때 오늘 날짜로 설정
);

// dateOfBirth가 변경되면 basicData 업데이트
watch(dateOfBirth, (newValue) => {
    if (isReadonlyField('DATE_OF_BIRTH')) return;
    const currentValue = props.modelValue || {} as any;
    const updated = {
        ...currentValue,
        DATE_OF_BIRTH: newValue || '',
        dateOfBirth: newValue || '',
    };
    emit('update:modelValue', updated);
});

const {
    dateValue: passportNoExpiryDate,
    displayValue: passportNoExpiryDateDisplay,
    menuOpen: passportNoExpiryDateMenu,
    calendarDate: passportNoExpiryDateCalendarDate
} = useDatePicker(
    computed(() => {
        const value = props.modelValue || {} as any;
        return value.PASSPORT_NO_EXPIRY_DATE || value.passportNoExpiryDate || '';
    }),
    true
);

watch(passportNoExpiryDate, (newValue) => {
    if (isReadonlyField('PASSPORT_NO_EXPIRY_DATE')) return;
    const currentValue = props.modelValue || {} as any;
    const updated = {
        ...currentValue,
        PASSPORT_NO_EXPIRY_DATE: newValue || '',
        passportNoExpiryDate: newValue || '',
    };
    emit('update:modelValue', updated);
});

// Date picker용 날짜 객체 (로컬 시간대 기준, 시간대 변환 방지)
const dateOfBirthPickerValue = computed(() => {
    if (!dateOfBirthCalendarDate.value) return new Date();
    // 날짜 문자열을 직접 파싱하여 로컬 시간대의 Date 객체 생성
    const [year, month, day] = dateOfBirthCalendarDate.value.split('-').map(Number);
    return new Date(year, month - 1, day);
});

const passportNoExpiryDatePickerValue = computed(() => {
    if (!passportNoExpiryDateCalendarDate.value) return new Date();
    const [year, month, day] = passportNoExpiryDateCalendarDate.value.split('-').map(Number);
    return new Date(year, month - 1, day);
});

// props.modelValue가 변경되면 dateOfBirth 업데이트 (Detail 페이지에서 데이터 로드 시)
watch(() => props.modelValue, (newValue) => {
    if (newValue) {
        const dateValue = (newValue as any).DATE_OF_BIRTH || (newValue as any).dateOfBirth || '';
        if (dateValue) {
            // ISO 형식 문자열을 YYYY-MM-DD로 변환
            const formatted = formatDateForInput(dateValue);
            // useDatePicker의 watch가 이미 처리했는지 확인하여 무한 루프 방지
            if (formatted && formatted !== dateOfBirth.value) {
                dateOfBirth.value = formatted;
            }
        } else if (dateOfBirth.value) {
            // 날짜가 제거된 경우
            dateOfBirth.value = '';
        }

        const passportExpiryValue = (newValue as any).PASSPORT_NO_EXPIRY_DATE || (newValue as any).passportNoExpiryDate || '';
        if (passportExpiryValue) {
            const formatted = formatDateForInput(passportExpiryValue);
            if (formatted && formatted !== passportNoExpiryDate.value) {
                passportNoExpiryDate.value = formatted;
            }
        } else if (passportNoExpiryDate.value) {
            passportNoExpiryDate.value = '';
        }
    }
}, { deep: true });

// 바인딩이 이미 수행되었는지 추적하는 플래그
const hasBoundValues = ref(false);

// 옵션 로드 완료 후 데이터 바인딩을 위한 함수 (Entity Detail의 jurisdiction 바인딩 방식 참고)
const bindValuesAfterOptionsLoaded = async () => {
    // 이미 바인딩이 완료되었으면 스킵
    if (hasBoundValues.value) {
        return;
    }
    
    const value = props.modelValue || {} as any;
    let needsUpdate = false;
    const updated: any = { ...value };
    
    // Identification Type 바인딩 (ID_TYPE_CD가 TOTAL_CODE인지 확인)
    if (value.ID_TYPE_CD) {
        const idTypeCd = String(value.ID_TYPE_CD).trim();
        // options에서 TOTAL_CODE로 존재하는지 확인
        const existsAsTotalCode = identificationTypeOptions.value.some((opt: any) => opt.TOTAL_CODE === idTypeCd);
        
        if (!existsAsTotalCode && identificationTypeOptions.value.length > 0) {
            // TOTAL_CODE가 아니면 CODE_NAME으로 찾아서 TOTAL_CODE로 변환
            const matchedOption = identificationTypeOptions.value.find((opt: any) => opt.CODE_NAME === idTypeCd);
            if (matchedOption && updated.ID_TYPE_CD !== matchedOption.TOTAL_CODE) {
                updated.ID_TYPE_CD = matchedOption.TOTAL_CODE;
                updated.idTypeCd = matchedOption.TOTAL_CODE;
                updated.identificationType = matchedOption.TOTAL_CODE;
                needsUpdate = true;
                console.log('✅ [bindValuesAfterOptionsLoaded] Identification Type bound:', matchedOption.TOTAL_CODE);
            }
        } else if (existsAsTotalCode) {
            // 이미 TOTAL_CODE이면 그대로 사용
            console.log('✅ [bindValuesAfterOptionsLoaded] Identification Type already TOTAL_CODE:', idTypeCd);
        }
    }
    
    // Nationality 바인딩 (NATIONALITY_CD가 TOTAL_CODE인지 확인)
    if (value.NATIONALITY_CD) {
        const nationalityCd = String(value.NATIONALITY_CD).trim();
        // options에서 TOTAL_CODE로 존재하는지 확인
        const existsAsTotalCode = nationalityOptions.value.some((opt: any) => opt.TOTAL_CODE === nationalityCd);
        
        if (!existsAsTotalCode && nationalityOptions.value.length > 0) {
            // TOTAL_CODE가 아니면 CODE_NAME으로 찾아서 TOTAL_CODE로 변환
            const matchedOption = nationalityOptions.value.find((opt: any) => opt.CODE_NAME === nationalityCd);
            if (matchedOption && updated.NATIONALITY_CD !== matchedOption.TOTAL_CODE) {
                updated.NATIONALITY_CD = matchedOption.TOTAL_CODE;
                updated.nationalityCd = matchedOption.TOTAL_CODE;
                updated.nationality = matchedOption.TOTAL_CODE;
                needsUpdate = true;
                console.log('✅ [bindValuesAfterOptionsLoaded] Nationality bound:', matchedOption.TOTAL_CODE);
            }
        } else if (existsAsTotalCode) {
            // 이미 TOTAL_CODE이면 그대로 사용
            console.log('✅ [bindValuesAfterOptionsLoaded] Nationality already TOTAL_CODE:', nationalityCd);
        }
    }
    
    // 업데이트가 필요한 경우에만 emit
    if (needsUpdate) {
        hasBoundValues.value = true;
        await nextTick();
        emit('update:modelValue', updated as CreateIndividualData | Contact);
    } else {
        // 업데이트가 필요 없어도 바인딩 시도는 완료
        hasBoundValues.value = true;
    }
};


// Validation Rules
const identificationTypeRules = ref<Array<(v: string) => boolean>>([]);

const firstNameRules = ref([
    (v: string) => !!v || (t.value('contact.First Name') || 'First Name') + ' ' + (t.value('contact.is required') || 'is required'),
    (v: string) => {
        if (!v) return true;
        const trimmed = v.trim();
        const nameRegex = /^[a-zA-Z가-힣\s]+$/;
        return nameRegex.test(trimmed) || (t.value('contact.can only contain letters and spaces') || 'can only contain letters and spaces');
    },
]);

const surnameRules = ref([
    (v: string) => !!v || (t.value('contact.Last Name') || 'Last Name') + ' ' + (t.value('contact.is required') || 'is required'),
    (v: string) => {
        if (!v) return true;
        const trimmed = v.trim();
        const nameRegex = /^[a-zA-Z가-힣\s]+$/;
        return nameRegex.test(trimmed) || (t.value('contact.can only contain letters and spaces') || 'can only contain letters and spaces');
    },
]);

const myContactNameRules = ref([
    (v: string) => !!v?.trim() || (t.value('contact.Name') || 'Name') + ' ' + (t.value('contact.is required') || 'is required'),
    (v: string) => {
        if (!v) return true;
        const trimmed = v.trim();
        const nameRegex = /^[a-zA-Z가-힣\s]+$/;
        return nameRegex.test(trimmed) || (t.value('contact.can only contain letters and spaces') || 'can only contain letters and spaces');
    },
]);

// Identification Type 목록 (B03 메인코드)
const identificationTypeOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

// Nationality 목록 (B04 메인코드)
const nationalityOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

type B15AutocompleteOption = {
    TOTAL_CODE: string;
    CODE_NAME: string;
    BIGO2: string;
};

/** MY Issuing Country / Place of Birth — Please Select 항목 제외, 영문(BIGO2) 검색 지원 */
const b15AutocompleteOptions = ref<B15AutocompleteOption[]>([]);

function b15AutocompleteFilter(
    _value: string,
    query: string,
    item?: { raw?: B15AutocompleteOption },
): boolean {
    const q = String(query ?? '').trim().toLowerCase();
    if (!q) return true;
    const raw = item?.raw;
    if (!raw) return false;
    const codeName = String(raw.CODE_NAME || '').toLowerCase();
    const bigo2 = String(raw.BIGO2 || '').toLowerCase();
    const totalCode = String(raw.TOTAL_CODE || '').toLowerCase();
    return codeName.includes(q) || bigo2.includes(q) || totalCode.includes(q);
}

const loadB15AutocompleteOptions = async () => {
    if (!isJurisdictionMY.value) {
        b15AutocompleteOptions.value = [];
        return;
    }
    try {
        const response = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
        const codes = (response as any).codes || response.data?.codes || [];

        const allOptions = codes
            .map((code: any) => ({
                TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
                CODE_NAME: String(
                    code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
                ).trim(),
                BIGO2: String(code.bigo2 || code.BIGO2 || '').trim(),
            }))
            .filter(
                (item: B15AutocompleteOption) =>
                    item.TOTAL_CODE && item.CODE_NAME && item.TOTAL_CODE.slice(-3) !== '000',
            );

        const uniqueCodesMap = new Map<string, B15AutocompleteOption>();
        allOptions.forEach((item: B15AutocompleteOption) => {
            if (!uniqueCodesMap.has(item.TOTAL_CODE)) {
                uniqueCodesMap.set(item.TOTAL_CODE, item);
            }
        });
        b15AutocompleteOptions.value = Array.from(uniqueCodesMap.values());
    } catch (error) {
        console.error('❌ [loadB15AutocompleteOptions] B15 로드 실패:', error);
        b15AutocompleteOptions.value = [];
    }
};

// Identification Type 로드 (MY일 때 M01, 아닐 때 B03)
const loadIdentificationTypes = async () => {
    try {
        // MY 세션일 때는 M01 (MY), 아닐 때는 B03 (CM) 사용
        const mainCode = isJurisdictionMY.value ? 'M01' : 'B03';
        const subCode = isJurisdictionMY.value ? 'MY' : 'CM';
        const response = await baseCodeApi.getCodesByMainCode(subCode, mainCode, undefined);
        const codes = (response as any).codes || response.data?.codes || [];
        
        // DEFAULT_CODE_NAME 우선 사용, 없으면 CODE_NAME 사용
        identificationTypeOptions.value = codes
            .map((code: any) => ({
                TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
                CODE_NAME: String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim()
            }))
            .filter((item: any) => {
                return item.TOTAL_CODE && item.CODE_NAME && 
                       item.TOTAL_CODE.length > 0 && item.CODE_NAME.length > 0;
            });
        
        // TOTAL_CODE 기준으로 중복 제거
        const uniqueCodesMap = new Map();
        identificationTypeOptions.value.forEach((item: any) => {
            if (!uniqueCodesMap.has(item.TOTAL_CODE)) {
                uniqueCodesMap.set(item.TOTAL_CODE, item);
            }
        });
        
        const allOptions = Array.from(uniqueCodesMap.values());
        
        // SUB_CODE가 "000"인 항목을 "Please Select"로 변경
        const processedOptions = allOptions.map((item: any) => {
            const subCode = item.TOTAL_CODE.slice(-3);
            if (subCode === '000') {
                return {
                    CODE_NAME: t.value('contact.Please Select') || 'Please Select',
                    TOTAL_CODE: ''
                };
            }
            return item;
        });
        
        identificationTypeOptions.value = processedOptions.filter((item: any) => {
            return item.CODE_NAME && item.CODE_NAME.length > 0;
        });
    } catch (error) {
        console.error('❌ [loadIdentificationTypes] Identification Type 로드 실패:', error);
        identificationTypeOptions.value = [];
    }
};

// Nationality 로드 (MY일 때 B15, 아닐 때 B04)
const loadNationalities = async () => {
    try {
        // MY 세션일 때는 B15, 아닐 때는 B04 사용
        const mainCode = isJurisdictionMY.value ? 'B15' : 'B04';
        const response = await baseCodeApi.getCodesByMainCode('CM', mainCode, undefined);
        const codes = (response as any).codes || response.data?.codes || [];
        
        // DEFAULT_CODE_NAME 우선 사용, 없으면 CODE_NAME 사용
        nationalityOptions.value = codes
            .map((code: any) => ({
                TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
                CODE_NAME: String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim()
            }))
            .filter((item: any) => {
                return item.TOTAL_CODE && item.CODE_NAME && 
                       item.TOTAL_CODE.length > 0 && item.CODE_NAME.length > 0;
            });
        
        // TOTAL_CODE 기준으로 중복 제거
        const uniqueCodesMap = new Map();
        nationalityOptions.value.forEach((item: any) => {
            if (!uniqueCodesMap.has(item.TOTAL_CODE)) {
                uniqueCodesMap.set(item.TOTAL_CODE, item);
            }
        });
        
        const allOptions = Array.from(uniqueCodesMap.values());
        
        // SUB_CODE가 "000"인 항목을 "Please Select"로 변경
        const processedOptions = allOptions.map((item: any) => {
            const subCode = item.TOTAL_CODE.slice(-3);
            if (subCode === '000') {
                return {
                    CODE_NAME: t.value('contact.Please Select') || 'Please Select',
                    TOTAL_CODE: ''
                };
            }
            return item;
        });
        
        nationalityOptions.value = processedOptions.filter((item: any) => {
            return item.CODE_NAME && item.CODE_NAME.length > 0;
        });
    } catch (error) {
        console.error('❌ [loadNationalities] Nationality 로드 실패:', error);
        nationalityOptions.value = [];
    }
};

// props.modelValue가 변경되면 바인딩 플래그 리셋 (새로운 데이터 로드 시)
watch(() => props.modelValue, (newValue) => {
    if (newValue) {
        // 새로운 데이터가 로드되면 바인딩 플래그 리셋
        hasBoundValues.value = false;
        // 옵션이 로드되어 있으면 다시 바인딩 시도
        if (identificationTypeOptions.value.length > 0 && nationalityOptions.value.length > 0) {
            bindValuesAfterOptionsLoaded();
        }
    }
}, { deep: true });

// 옵션이 로드되면 자동으로 값 바인딩 시도
watch([identificationTypeOptions, nationalityOptions], async () => {
    const allOptionsLoaded = identificationTypeOptions.value.length > 0 && nationalityOptions.value.length > 0;
    if (props.modelValue && allOptionsLoaded && !hasBoundValues.value) {
        await bindValuesAfterOptionsLoaded();
    }
}, { deep: true });

// isJurisdictionMY가 변경될 때 옵션 다시 로드
watch(isJurisdictionMY, async (newValue, oldValue) => {
    if (newValue !== oldValue) {
        // jurisdiction이 변경되면 옵션 다시 로드
        hasBoundValues.value = false;
        await Promise.all([
            loadIdentificationTypes(),
            loadNationalities(),
            loadB15AutocompleteOptions(),
        ]);
        // 옵션 로드 완료 후 값 바인딩
        await bindValuesAfterOptionsLoaded();
    }
}, { immediate: false });

onMounted(async () => {
    // 옵션 로드
    await Promise.all([
        loadIdentificationTypes(),
        loadNationalities(),
        loadB15AutocompleteOptions(),
    ]);
    
    // 옵션 로드 완료 후 값 바인딩
    await bindValuesAfterOptionsLoaded();
});

// KYC Status (임시로 'Complete'로 설정)
const kycStatus = ref('Complete');

// Legal Name / Name 필드 라벨 및 값 계산
const bizFileNameLabel = computed(() => {
    const value = props.modelValue || {} as any;
    const registerType = value.CONTACT_REGISTER_TYPE || value.contactRegisterType || '';
    if (registerType === 'A06001') {
        return t.value('contact.Legal Name') || 'Legal Name';
    }
    return t.value('contact.Name') || 'Name';
});

const bizFileNameValue = computed(() => {
    const value = props.modelValue || {} as any;
    // CONTACT_NAME 컬럼 사용
    return value.CONTACT_NAME || value.contactName || '';
});

const showDetailLayout = computed(() => props.detailMode);

const myLicenseCombinedFromModel = computed(() => {
    const value = props.modelValue || ({} as any);
    return String(
        value.PROFILE_SECRETARY_LICENSE_NO || value.profileSecretaryLicenseNo || value.license_number || ''
    ).trim();
});

const myQualificationPrefix = ref<MyQualificationPrefix>('');
const myLicenseDigitsOnly = ref('');
const myQualificationItems = [...MY_LICENSE_QUALIFICATION_PREFIXES];

watch(
    myLicenseCombinedFromModel,
    (lic) => {
        const { prefix, digits } = splitMySecretaryLicense(lic);
        myQualificationPrefix.value = prefix;
        myLicenseDigitsOnly.value = digits;
    },
    { immediate: true }
);

function emitMyMergedLicense() {
    if (isReadonlyField('PROFILE_SECRETARY_LICENSE_NO')) return;
    const current = props.modelValue || ({} as any);
    const merged = mergeMySecretaryLicense(myQualificationPrefix.value, myLicenseDigitsOnly.value);
    emit('update:modelValue', {
        ...current,
        PROFILE_SECRETARY_LICENSE_NO: merged,
        profileSecretaryLicenseNo: merged,
        license_number: merged,
    } as CreateIndividualData | Contact);
}

function onMyQualificationChange(val: unknown) {
    if (isReadonlyField('PROFILE_SECRETARY_LICENSE_NO')) return;
    const next = String(val ?? '').trim().toUpperCase();
    myQualificationPrefix.value = MY_LICENSE_QUALIFICATION_PREFIXES.includes(next as (typeof MY_LICENSE_QUALIFICATION_PREFIXES)[number])
        ? (next as (typeof MY_LICENSE_QUALIFICATION_PREFIXES)[number])
        : '';
    emitMyMergedLicense();
}

function onMyLicenseDigitsChange(val: unknown) {
    if (isReadonlyField('PROFILE_SECRETARY_LICENSE_NO')) return;
    myLicenseDigitsOnly.value = String(val ?? '').replace(/\D/g, '');
    emitMyMergedLicense();
}

// Identification Type 표시 이름 (readonly일 때 사용)
const identificationTypeDisplayName = computed(() => {
    const idTypeCode = basicData.value.ID_TYPE_CD;
    if (!idTypeCode) return '';
    const option = identificationTypeOptions.value.find(opt => opt.TOTAL_CODE === idTypeCode);
    return option ? option.CODE_NAME : idTypeCode;
});

// Nationality 표시 이름 (readonly일 때 사용)
const nationalityDisplayName = computed(() => {
    const nationalityCode = basicData.value.NATIONALITY_CD;
    if (!nationalityCode) return '';
    const option = nationalityOptions.value.find(opt => opt.TOTAL_CODE === nationalityCode);
    return option ? option.CODE_NAME : nationalityCode;
});

watch(
    () => resolveContactIdTypeCd(props.modelValue),
    (idType) => {
        if (!isJurisdictionMY.value) return;
        if (idType === MY_PASSPORT_ID_TYPE_CD) return;

        const hasIssuing = Boolean(String(basicData.value.PROFILE_ISSUING_COUNTRY || '').trim());
        const hasExpiry = Boolean(String(basicData.value.PASSPORT_NO_EXPIRY_DATE || '').trim());
        if (!hasIssuing && !hasExpiry) return;

        const current = basicData.value;
        basicData.value = {
            ...current,
            PROFILE_ISSUING_COUNTRY: '',
            profileIssuingCountry: '',
            PASSPORT_NO_EXPIRY_DATE: '',
            passportNoExpiryDate: '',
        };
        passportNoExpiryDate.value = '';
    },
);

useMyNricDateOfBirthAutoFill({
    enabled: computed(() => isJurisdictionMY.value && !isReadonlyField('DATE_OF_BIRTH')),
    idTypeCd: computed(() => basicData.value.ID_TYPE_CD),
    identificationNo: computed(() => basicData.value.CONTACT_REG_NO),
    setDateOfBirth: (ymd) => {
        if (ymd !== dateOfBirth.value) {
            dateOfBirth.value = ymd;
        }
    },
});
</script>

<template>
    <v-card class="contact-basic-section">
        <v-card-title class="contact-section-title d-flex justify-space-between align-center">
            <span>{{ t('contact.Basic') || 'Basic' }}</span>
            <div v-if="!hideKycStatus && !showDetailLayout" class="d-flex align-center">
                <v-chip class="kyc-chip" variant="flat" size="small">
                    {{ t('contact.KYC Complete') || 'KYC Complete' }}
                </v-chip>
            </div>
        </v-card-title>
        <v-card-text>
            <v-row>
                <!-- Detail 페이지: BizFile Name / Name 필드 (디테일 모드에서만 표시) -->
                <template v-if="showDetailLayout">
                    <v-col cols="12">
                        <div class="d-flex flex-column flex-md-row align-md-center">
                            <div class="flex-grow-1 mr-md-4 mb-3 mb-md-0">
                                <v-text-field
                                    :model-value="bizFileNameValue"
                                    :label="bizFileNameLabel"
                                    readonly
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                    class="biz-file-field"
                                />
                            </div>
                            <div v-if="!hideKycStatus" class="kyc-pill-wrapper d-flex justify-end">
                                <v-chip class="kyc-chip" variant="flat" size="small">
                                    {{ t('contact.KYC Complete') || 'KYC Complete' }}
                                </v-chip>
                            </div>
                        </div>
                    </v-col>
                </template>
                
                <!-- Create 페이지 레이아웃 -->
                <template v-else>
                    <!-- Row 1: Identification Type, Nationality/Citizenship (2개) -->
                    <v-col cols="12" md="6">
                        <!-- Readonly일 때는 텍스트 필드로 표시 -->
                        <v-text-field
                            v-if="isReadonlyField('ID_TYPE_CD')"
                            :model-value="identificationTypeDisplayName"
                            :label="t('contact.Identification Type') || 'Identification Type'"
                            :readonly="true"
                            :class="{ 'readonly-field': true }"
                            variant="outlined"
                            density="comfortable"
                        >
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('identification_type')" />
                            </template>
                        </v-text-field>
                        <!-- 수정 가능할 때는 select 사용 -->
                        <v-select
                            v-else
                            :model-value="basicData.ID_TYPE_CD"
                            :items="identificationTypeOptions"
                            item-title="CODE_NAME"
                            item-value="TOTAL_CODE"
                            :placeholder="t('contact.Please Select') || 'Please Select'"
                            :rules="identificationTypeRules"
                            variant="outlined"
                            density="comfortable"
                            @update:model-value="(val: any) => {
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    ID_TYPE_CD: val ?? ''
                                };
                            }"
                        >
                            <template v-slot:label>
                                <span>{{ t('contact.Identification Type') || 'Identification Type' }}</span>
                            </template>
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('identification_type')" />
                            </template>
                        </v-select>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                        <!-- Readonly일 때는 텍스트 필드로 표시 -->
                        <v-text-field
                            v-if="isReadonlyField('NATIONALITY_CD')"
                            :model-value="nationalityDisplayName"
                            :label="t('contact.Nationality/Citizenship') || 'Nationality/Citizenship'"
                            :readonly="true"
                            :class="{ 'readonly-field': true }"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            class="mb-4"
                        >
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('nationality')" />
                            </template>
                        </v-text-field>
                        <!-- 수정 가능할 때는 autocomplete 사용 -->
                        <v-autocomplete
                            v-else
                            :model-value="basicData.NATIONALITY_CD"
                            :items="nationalityOptions"
                            item-title="CODE_NAME"
                            item-value="TOTAL_CODE"
                            :label="t('contact.Nationality/Citizenship') || 'Nationality/Citizenship'"
                            placeholder="Please Select"
                            required
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            class="mb-4"
                            clearable
                            @update:model-value="(val: any) => {
                                const current = basicData;
                                const next = val == null || val === '' ? null : val;
                                basicData = {
                                    ...current,
                                    NATIONALITY_CD: next
                                };
                            }"
                        />
                    </v-col>
                </template>

                <template v-if="!showDetailLayout">
                    <v-col cols="12" md="6">
                        <v-text-field
                            :model-value="basicData.CONTACT_REG_NO"
                            :readonly="readonly || readonlyFields.includes('CONTACT_REG_NO')"
                            :class="{ 'readonly-field': isReadonlyField('CONTACT_REG_NO'), 'identification-no-field': true }"
                            variant="outlined"
                            density="comfortable"
                            @input="(event: any) => {
                                if (readonly || readonlyFields.includes('CONTACT_REG_NO')) return;
                                const inputValue = event.target?.value ?? '';
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    CONTACT_REG_NO: inputValue
                                };
                            }"
                            @update:model-value="(val: any) => {
                                if (readonly || readonlyFields.includes('CONTACT_REG_NO')) return;
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    CONTACT_REG_NO: val ?? ''
                                };
                            }"
                        >
                            <template v-slot:label>
                                <span>{{ t('contact.Identification No') || 'Identification No.' }}</span>
                            </template>
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('identification_number')" />
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-if="isReadonlyField('DATE_OF_BIRTH')"
                            :model-value="dateOfBirthDisplay"
                            :label="t('contact.Date of Birth') || 'Date of Birth'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="true"
                            :class="{ 'readonly-field': true }"
                            hide-details
                            prepend-inner-icon="mdi-calendar"
                        />
                        <v-menu
                            v-else
                            v-model="dateOfBirthMenu"
                            :close-on-content-click="false"
                            location="bottom"
                        >
                            <template v-slot:activator="{ props: activatorProps }">
                                <v-text-field
                                    v-model="dateOfBirthDisplay"
                                    :label="t('contact.Date of Birth') || 'Date of Birth'"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                    prepend-inner-icon="mdi-calendar"
                                    v-bind="activatorProps"
                                />
                            </template>
                            <v-date-picker
                                :model-value="dateOfBirthPickerValue"
                                @update:model-value="(val: Date | string) => {
                                    let dateStr: string;
                                    if (val instanceof Date) {
                                        const year = val.getFullYear();
                                        const month = String(val.getMonth() + 1).padStart(2, '0');
                                        const day = String(val.getDate()).padStart(2, '0');
                                        dateStr = `${year}-${month}-${day}`;
                                    } else {
                                        dateStr = val;
                                    }
                                    dateOfBirth = dateStr;
                                    dateOfBirthCalendarDate = dateStr;
                                    dateOfBirthMenu = false;
                                }"
                            />
                        </v-menu>
                    </v-col>
                </template>
                
                <!-- MY Create: 단일 Name → CONTACT_NAME 저장 -->
                <v-col v-if="showMySingleNameField" cols="12">
                    <v-text-field
                        :model-value="basicData.CONTACT_NAME"
                        :rules="myContactNameRules"
                        variant="outlined"
                        density="comfortable"
                        @input="(event: any) => {
                            const inputValue = event.target?.value ?? '';
                            const current = basicData;
                            basicData = { ...current, CONTACT_NAME: inputValue };
                        }"
                        @update:model-value="(val: any) => {
                            const current = basicData;
                            basicData = { ...current, CONTACT_NAME: val ?? '' };
                        }"
                    >
                        <template v-slot:label>
                            <span>{{ t('contact.Name') || 'Name' }} <span class="required-asterisk">*</span></span>
                        </template>
                    </v-text-field>
                </v-col>

                <!-- Row 2: Name Fields - 한 줄로 배치 (3개) — 비-MY만 -->
                <v-col v-if="!hideSplitNameFields" cols="12" md="4">
                    <v-text-field
                        :model-value="basicData.FIRST_NAME"
                        :rules="firstNameRules"
                        :readonly="readonly || readonlyFields.includes('FIRST_NAME')"
                        :class="{ 'readonly-field': isReadonlyField('FIRST_NAME') }"
                        required
                        variant="outlined"
                        density="comfortable"
                        @input="(event: any) => {
                            if (readonly || readonlyFields.includes('FIRST_NAME')) return;
                            // 한글 입력 완료 후에만 업데이트 (Entity Detail 방식 참고)
                            const inputValue = event.target?.value ?? '';
                            const current = basicData;
                            basicData = {
                                ...current,
                                FIRST_NAME: inputValue
                            };
                        }"
                        @update:model-value="(val: any) => {
                            if (readonly || readonlyFields.includes('FIRST_NAME')) return;
                            const current = basicData;
                            basicData = {
                                ...current,
                                FIRST_NAME: val ?? ''
                            };
                        }"
                    >
                        <template v-slot:label>
                            <span>{{ t('contact.First Name') || 'First Name' }} <span class="required-asterisk">*</span></span>
                        </template>
                    </v-text-field>
                </v-col>
                
                <v-col v-if="!hideSplitNameFields" cols="12" md="4">
                    <v-text-field
                        :model-value="basicData.MIDDLE_NAME"
                        :label="t('contact.Middle Name') || 'Middle Name'"
                        :readonly="readonly || readonlyFields.includes('MIDDLE_NAME')"
                        :class="{ 'readonly-field': isReadonlyField('MIDDLE_NAME') }"
                        variant="outlined"
                        density="comfortable"
                        @input="(event: any) => {
                            if (readonly || readonlyFields.includes('MIDDLE_NAME')) return;
                            // 한글 입력 완료 후에만 업데이트 (Entity Detail 방식 참고)
                            const inputValue = event.target?.value ?? '';
                            const current = basicData;
                            basicData = {
                                ...current,
                                MIDDLE_NAME: inputValue
                            };
                        }"
                        @update:model-value="(val: any) => {
                            if (readonly || readonlyFields.includes('MIDDLE_NAME')) return;
                            const current = basicData;
                            basicData = {
                                ...current,
                                MIDDLE_NAME: val ?? ''
                            };
                        }"
                    />
                </v-col>
                
                <v-col v-if="!hideSplitNameFields" cols="12" md="4">
                    <v-text-field
                        :model-value="basicData.SURNAME"
                        :rules="surnameRules"
                        :readonly="readonly || readonlyFields.includes('SURNAME')"
                        :class="{ 'readonly-field': isReadonlyField('SURNAME') }"
                        required
                        variant="outlined"
                        density="comfortable"
                        @input="(event: any) => {
                            if (readonly || readonlyFields.includes('SURNAME')) return;
                            // 한글 입력 완료 후에만 업데이트 (Entity Detail 방식 참고)
                            const inputValue = event.target?.value ?? '';
                            const current = basicData;
                            basicData = {
                                ...current,
                                SURNAME: inputValue
                            };
                        }"
                        @update:model-value="(val: any) => {
                            if (readonly || readonlyFields.includes('SURNAME')) return;
                            const current = basicData;
                            basicData = {
                                ...current,
                                SURNAME: val ?? ''
                            };
                        }"
                    >
                        <template v-slot:label>
                            <span>{{ t('contact.Last Name') || 'Last Name' }} <span class="required-asterisk">*</span></span>
                        </template>
                    </v-text-field>
                </v-col>
                
                <!-- Detail 페이지: ID Type / Identification No / Passport Expiry -->
                <template v-if="showDetailLayout">
                    <v-col cols="12" :md="myIdRowColSpan">
                        <!-- Readonly일 때는 텍스트 필드로 표시 -->
                        <v-text-field
                            v-if="isReadonlyField('ID_TYPE_CD')"
                            :model-value="identificationTypeDisplayName"
                            :label="t('contact.Identification Type') || 'Identification Type'"
                            :readonly="true"
                            :class="{ 'readonly-field': true }"
                            variant="outlined"
                            density="comfortable"
                        >
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('identification_type')" />
                            </template>
                        </v-text-field>
                        <!-- 수정 가능할 때는 select 사용 -->
                        <v-select
                            v-else
                            :model-value="basicData.ID_TYPE_CD"
                            :items="identificationTypeOptions"
                            item-title="CODE_NAME"
                            item-value="TOTAL_CODE"
                            :placeholder="t('contact.Please Select') || 'Please Select'"
                            :rules="identificationTypeRules"
                            variant="outlined"
                            density="comfortable"
                            @update:model-value="(val: any) => {
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    ID_TYPE_CD: val ?? ''
                                };
                            }"
                        >
                            <template v-slot:label>
                                <span>{{ t('contact.Identification Type') || 'Identification Type' }}</span>
                            </template>
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('identification_type')" />
                            </template>
                        </v-select>
                    </v-col>
                    
                    <v-col cols="12" :md="myIdRowColSpan">
                        <v-text-field
                            :model-value="basicData.CONTACT_REG_NO"
                            :readonly="readonly || readonlyFields.includes('CONTACT_REG_NO')"
                            :class="{ 'readonly-field': isReadonlyField('CONTACT_REG_NO'), 'identification-no-field': true }"
                            variant="outlined"
                            density="comfortable"
                            @input="(event: any) => {
                                if (readonly || readonlyFields.includes('CONTACT_REG_NO')) return;
                                const inputValue = event.target?.value ?? '';
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    CONTACT_REG_NO: inputValue
                                };
                            }"
                            @update:model-value="(val: any) => {
                                if (readonly || readonlyFields.includes('CONTACT_REG_NO')) return;
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    CONTACT_REG_NO: val ?? ''
                                };
                            }"
                        >
                            <template v-slot:label>
                                <span>{{ t('contact.Identification No') || 'Identification No.' }}</span>
                            </template>
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('identification_number')" />
                            </template>
                        </v-text-field>
                    </v-col>

                    <template v-if="showPassportExpiryField">
                        <v-col v-if="showMyPassportFields" cols="12" md="6">
                            <v-autocomplete
                                :model-value="basicData.PROFILE_ISSUING_COUNTRY || null"
                                :items="b15AutocompleteOptions"
                                item-title="CODE_NAME"
                                item-value="TOTAL_CODE"
                                :custom-filter="b15AutocompleteFilter"
                                :readonly="effectiveFormReadonly"
                                :class="{ 'readonly-field': effectiveFormReadonly }"
                                :label="t('register.detail.issuingCountry') || 'Issuing Country'"
                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                clearable
                                :menu-props="{ maxHeight: 240 }"
                                @update:model-value="(val: any) => {
                                    if (effectiveFormReadonly) return;
                                    const current = basicData;
                                    basicData = {
                                        ...current,
                                        PROFILE_ISSUING_COUNTRY: val == null || val === '' ? '' : String(val)
                                    };
                                }"
                            />
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-text-field
                                v-if="isReadonlyField('PASSPORT_NO_EXPIRY_DATE')"
                                :model-value="passportNoExpiryDateDisplay"
                                :label="t('contact.Passport No Expiry Date') || 'Passport No Expiry Date'"
                                variant="outlined"
                                density="comfortable"
                                :readonly="true"
                                :class="{ 'readonly-field': true }"
                                hide-details
                                prepend-inner-icon="mdi-calendar"
                            />
                            <v-menu
                                v-else
                                v-model="passportNoExpiryDateMenu"
                                :close-on-content-click="false"
                                location="bottom"
                            >
                                <template v-slot:activator="{ props: activatorProps }">
                                    <v-text-field
                                        v-model="passportNoExpiryDateDisplay"
                                        :label="t('contact.Passport No Expiry Date') || 'Passport No Expiry Date'"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                        prepend-inner-icon="mdi-calendar"
                                        v-bind="activatorProps"
                                    />
                                </template>
                                <v-date-picker
                                    :model-value="passportNoExpiryDatePickerValue"
                                    @update:model-value="(val: Date | string) => {
                                        let dateStr: string;
                                        if (val instanceof Date) {
                                            const year = val.getFullYear();
                                            const month = String(val.getMonth() + 1).padStart(2, '0');
                                            const day = String(val.getDate()).padStart(2, '0');
                                            dateStr = `${year}-${month}-${day}`;
                                        } else {
                                            dateStr = val;
                                        }
                                        passportNoExpiryDate = dateStr;
                                        passportNoExpiryDateCalendarDate = dateStr;
                                        passportNoExpiryDateMenu = false;
                                    }"
                                />
                            </v-menu>
                        </v-col>
                    </template>

                    <v-col cols="12" md="6">
                        <!-- Readonly일 때는 텍스트 필드로 표시 -->
                        <v-text-field
                            v-if="isReadonlyField('NATIONALITY_CD')"
                            :model-value="nationalityDisplayName"
                            :label="t('contact.Nationality/Citizenship') || 'Nationality/Citizenship'"
                            :readonly="true"
                            :class="{ 'readonly-field': true }"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            class="mb-4"
                        >
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('nationality')" />
                            </template>
                        </v-text-field>
                        <!-- 수정 가능할 때는 autocomplete 사용 -->
                        <v-autocomplete
                            v-else
                            :model-value="basicData.NATIONALITY_CD"
                            :items="nationalityOptions"
                            item-title="CODE_NAME"
                            item-value="TOTAL_CODE"
                            :label="t('contact.Nationality/Citizenship') || 'Nationality/Citizenship'"
                            placeholder="Please Select"
                            required
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            class="mb-4"
                            clearable
                            @update:model-value="(val: any) => {
                                const current = basicData;
                                const next = val == null || val === '' ? null : val;
                                basicData = {
                                    ...current,
                                    NATIONALITY_CD: next
                                };
                            }"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                    <!-- Readonly일 때는 달력 없이 일반 텍스트 필드로 표시 -->
                    <v-text-field
                        v-if="isReadonlyField('DATE_OF_BIRTH')"
                        :model-value="dateOfBirthDisplay"
                        :label="t('contact.Date of Birth') || 'Date of Birth'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="true"
                        :class="{ 'readonly-field': true }"
                        hide-details
                        prepend-inner-icon="mdi-calendar"
                    >
                        <template #append-inner>
                            <SyncFieldIndicator :change="syncChange('date_of_birth')" />
                        </template>
                    </v-text-field>
                    <!-- 수정 가능할 때는 달력 메뉴 사용 -->
                    <v-menu
                        v-else
                        v-model="dateOfBirthMenu"
                        :close-on-content-click="false"
                        location="bottom"
                    >
                        <template v-slot:activator="{ props: activatorProps }">
                            <v-text-field
                                v-model="dateOfBirthDisplay"
                                :label="t('contact.Date of Birth') || 'Date of Birth'"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                prepend-inner-icon="mdi-calendar"
                                v-bind="activatorProps"
                            />
                        </template>
                        <v-date-picker
                            :model-value="dateOfBirthPickerValue"
                            @update:model-value="(val: Date | string) => { 
                                let dateStr: string;
                                if (val instanceof Date) {
                                    // 로컬 시간대 기준으로 날짜 문자열 생성 (UTC 변환 방지)
                                    const year = val.getFullYear();
                                    const month = String(val.getMonth() + 1).padStart(2, '0');
                                    const day = String(val.getDate()).padStart(2, '0');
                                    dateStr = `${year}-${month}-${day}`;
                                } else {
                                    dateStr = val;
                                }
                                dateOfBirth = dateStr;
                                dateOfBirthCalendarDate = dateStr;
                                dateOfBirthMenu = false; 
                            }"
                        />
                    </v-menu>
                </v-col>
                </template>

                <!-- 비MY jurisdiction: Passport Expiry 항상 표시 (MY는 아래 showMyProfileFields 블록에서 처리) -->
                <v-col v-if="!showDetailLayout && !isJurisdictionMY" cols="12" md="6">
                    <v-text-field
                        v-if="isReadonlyField('PASSPORT_NO_EXPIRY_DATE')"
                        :model-value="passportNoExpiryDateDisplay"
                        :label="t('contact.Passport No Expiry Date') || 'Passport No Expiry Date'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="true"
                        :class="{ 'readonly-field': true }"
                        hide-details
                        prepend-inner-icon="mdi-calendar"
                    />
                    <v-menu
                        v-else
                        v-model="passportNoExpiryDateMenu"
                        :close-on-content-click="false"
                        location="bottom"
                    >
                        <template v-slot:activator="{ props: activatorProps }">
                            <v-text-field
                                v-model="passportNoExpiryDateDisplay"
                                :label="t('contact.Passport No Expiry Date') || 'Passport No Expiry Date'"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                prepend-inner-icon="mdi-calendar"
                                v-bind="activatorProps"
                            />
                        </template>
                        <v-date-picker
                            :model-value="passportNoExpiryDatePickerValue"
                            @update:model-value="(val: Date | string) => {
                                let dateStr: string;
                                if (val instanceof Date) {
                                    const year = val.getFullYear();
                                    const month = String(val.getMonth() + 1).padStart(2, '0');
                                    const day = String(val.getDate()).padStart(2, '0');
                                    dateStr = `${year}-${month}-${day}`;
                                } else {
                                    dateStr = val;
                                }
                                passportNoExpiryDate = dateStr;
                                passportNoExpiryDateCalendarDate = dateStr;
                                passportNoExpiryDateMenu = false;
                            }"
                        />
                    </v-menu>
                </v-col>

                <template v-if="showMyProfileFields">
                    <template v-if="showMyPassportFields && !showDetailLayout">
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                :model-value="basicData.PROFILE_ISSUING_COUNTRY || null"
                                :items="b15AutocompleteOptions"
                                item-title="CODE_NAME"
                                item-value="TOTAL_CODE"
                                :custom-filter="b15AutocompleteFilter"
                                :readonly="effectiveFormReadonly"
                                :class="{ 'readonly-field': effectiveFormReadonly }"
                                :label="t('register.detail.issuingCountry') || 'Issuing Country'"
                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                clearable
                                :menu-props="{ maxHeight: 240 }"
                                @update:model-value="(val: any) => {
                                    if (effectiveFormReadonly) return;
                                    const current = basicData;
                                    basicData = {
                                        ...current,
                                        PROFILE_ISSUING_COUNTRY: val == null || val === '' ? '' : String(val)
                                    };
                                }"
                            />
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-text-field
                                v-if="isReadonlyField('PASSPORT_NO_EXPIRY_DATE')"
                                :model-value="passportNoExpiryDateDisplay"
                                :label="t('contact.Passport No Expiry Date') || 'Passport No Expiry Date'"
                                variant="outlined"
                                density="comfortable"
                                :readonly="true"
                                :class="{ 'readonly-field': true }"
                                hide-details
                                prepend-inner-icon="mdi-calendar"
                            />
                            <v-menu
                                v-else
                                v-model="passportNoExpiryDateMenu"
                                :close-on-content-click="false"
                                location="bottom"
                            >
                                <template v-slot:activator="{ props: activatorProps }">
                                    <v-text-field
                                        v-model="passportNoExpiryDateDisplay"
                                        :label="t('contact.Passport No Expiry Date') || 'Passport No Expiry Date'"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                        prepend-inner-icon="mdi-calendar"
                                        v-bind="activatorProps"
                                    />
                                </template>
                                <v-date-picker
                                    :model-value="passportNoExpiryDatePickerValue"
                                    @update:model-value="(val: Date | string) => {
                                        let dateStr: string;
                                        if (val instanceof Date) {
                                            const year = val.getFullYear();
                                            const month = String(val.getMonth() + 1).padStart(2, '0');
                                            const day = String(val.getDate()).padStart(2, '0');
                                            dateStr = `${year}-${month}-${day}`;
                                        } else {
                                            dateStr = val;
                                        }
                                        passportNoExpiryDate = dateStr;
                                        passportNoExpiryDateCalendarDate = dateStr;
                                        passportNoExpiryDateMenu = false;
                                    }"
                                />
                            </v-menu>
                        </v-col>
                    </template>

                    <v-col cols="12" md="6">
                        <v-autocomplete
                            :model-value="basicData.PROFILE_PLACE_OF_BIRTH || null"
                            :items="b15AutocompleteOptions"
                            item-title="CODE_NAME"
                            item-value="TOTAL_CODE"
                            :custom-filter="b15AutocompleteFilter"
                            :readonly="effectiveFormReadonly"
                            :class="{ 'readonly-field': effectiveFormReadonly }"
                            :label="t('register.detail.placeOfBirth') || 'Place of Birth'"
                            :placeholder="t('contact.Please Select') || 'Please Select'"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            clearable
                            :menu-props="{ maxHeight: 240 }"
                            @update:model-value="(val: any) => {
                                if (effectiveFormReadonly) return;
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    PROFILE_PLACE_OF_BIRTH: val == null || val === '' ? '' : String(val)
                                };
                            }"
                        />
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-select
                            :model-value="basicData.PROFILE_GENDER"
                            :items="genderOptions"
                            :readonly="effectiveFormReadonly"
                            :class="{ 'readonly-field': effectiveFormReadonly }"
                            :label="t('contact.Gender') || 'Gender'"
                            :placeholder="t('contact.Please Select') || 'Please Select'"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            @update:model-value="(val: any) => {
                                if (effectiveFormReadonly) return;
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    PROFILE_GENDER: String(val ?? '')
                                };
                            }"
                        >
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('gender')" />
                            </template>
                        </v-select>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-select
                            :model-value="basicData.PROFILE_RACE"
                            :items="raceOptions"
                            :readonly="effectiveFormReadonly"
                            :class="{ 'readonly-field': effectiveFormReadonly }"
                            :label="t('contact.Race') || 'Race'"
                            :placeholder="t('contact.Please Select') || 'Please Select'"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            @update:model-value="(val: any) => {
                                if (effectiveFormReadonly) return;
                                const current = basicData;
                                basicData = {
                                    ...current,
                                    PROFILE_RACE: String(val ?? '')
                                };
                            }"
                        >
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('race')" />
                            </template>
                        </v-select>
                    </v-col>

                    <!--
                        MY Contact Detail — L_GLOBAL_CONTACT_PROFILE.JSON_DATA
                        (license_number / SecretaryCertificateNo) 로 바인딩되는
                        Qualification Type / License No. / Practising Cert. No.
                        cols="12" 로 flex 줄바꿈을 강제 → Race 다음에 항상 새 줄에서 시작.
                        내부 v-row 로 3필드를 한 줄(md=4×3)에 배치.
                        Entity Officer Secretary 오버레이(L_OFFICER 기반) 표시 시엔 중복 방지를 위해 숨김.
                    -->
                    <v-col v-if="!showEntityOfficerSecretaryFields" cols="12">
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-select
                                    :model-value="myQualificationPrefix || undefined"
                                    :items="myQualificationItems"
                                    :readonly="effectiveFormReadonly || isReadonlyField('PROFILE_SECRETARY_LICENSE_NO')"
                                    :class="{ 'readonly-field': effectiveFormReadonly || isReadonlyField('PROFILE_SECRETARY_LICENSE_NO') }"
                                    :label="t('contact.Qualification Type') || 'Qualification Type'"
                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                    clearable
                                    :menu-props="{ maxHeight: 240 }"
                                    @update:model-value="onMyQualificationChange"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    :model-value="myLicenseDigitsOnly"
                                    :readonly="effectiveFormReadonly || isReadonlyField('PROFILE_SECRETARY_LICENSE_NO')"
                                    :class="{ 'readonly-field': effectiveFormReadonly || isReadonlyField('PROFILE_SECRETARY_LICENSE_NO') }"
                                    :label="t('contact.License No.') || 'License No.'"
                                    inputmode="numeric"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                    @update:model-value="onMyLicenseDigitsChange"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    :model-value="basicData.PROFILE_SECRETARY_CERTIFICATE_NO || ''"
                                    :readonly="effectiveFormReadonly || isReadonlyField('PROFILE_SECRETARY_CERTIFICATE_NO')"
                                    :class="{ 'readonly-field': effectiveFormReadonly || isReadonlyField('PROFILE_SECRETARY_CERTIFICATE_NO') }"
                                    :label="t('contact.Practising Cert. No.') || 'Practising Cert. No.'"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                    @update:model-value="(val: any) => {
                                        if (effectiveFormReadonly) return;
                                        if (isReadonlyField('PROFILE_SECRETARY_CERTIFICATE_NO')) return;
                                        const current = basicData;
                                        basicData = {
                                            ...current,
                                            PROFILE_SECRETARY_CERTIFICATE_NO: String(val ?? ''),
                                        } as any;
                                    }"
                                />
                            </v-col>
                        </v-row>
                    </v-col>

                    <v-col
                        v-if="showEntityOfficerDirectorFields || showEntityOfficerManagerFields"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            :model-value="entityOfficerOverlay?.occupation || ''"
                            :readonly="effectiveFormReadonly"
                            :class="{ 'readonly-field': effectiveFormReadonly }"
                            :label="t('contact.Occupation') || 'Occupation'"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            @update:model-value="(val: string) => {
                                if (effectiveFormReadonly) return;
                                patchEntityOfficerOverlay({ occupation: String(val ?? '') });
                            }"
                        />
                    </v-col>

                    <v-col
                        v-if="showEntityOfficerSecretaryFields"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            :model-value="entityOfficerOverlay?.occupation || ''"
                            :readonly="effectiveFormReadonly"
                            :class="{ 'readonly-field': effectiveFormReadonly }"
                            :label="t('contact.Occupation') || 'Occupation'"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            @update:model-value="(val: string) => {
                                if (effectiveFormReadonly) return;
                                patchEntityOfficerOverlay({ occupation: String(val ?? '') });
                            }"
                        />
                    </v-col>

                    <v-col
                        v-if="showEntityBeneficialOwnerFields"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            :model-value="entityBeneficialOwnerOverlay?.occupation || ''"
                            :readonly="effectiveFormReadonly"
                            :class="{ 'readonly-field': effectiveFormReadonly }"
                            :label="t('contact.Occupation') || 'Occupation'"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            @update:model-value="(val: string) => {
                                if (effectiveFormReadonly) return;
                                patchEntityBeneficialOwnerOverlay({ occupation: String(val ?? '') });
                            }"
                        />
                    </v-col>

                    <template v-if="showEntityOfficerSecretaryFields">
                        <v-col cols="12" md="4">
                            <v-select
                                :model-value="entityOfficerQualificationPrefix || undefined"
                                :items="entityOfficerQualificationItems"
                                :readonly="effectiveFormReadonly"
                                :class="{ 'readonly-field': effectiveFormReadonly }"
                                :label="`${t('contact.Qualification Type') || 'Qualification Type'} *`"
                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                @update:model-value="onEntityOfficerQualificationChange"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                :model-value="entityOfficerLicenseDigits"
                                :readonly="effectiveFormReadonly"
                                :class="{ 'readonly-field': effectiveFormReadonly }"
                                :label="t('contact.License No.') || 'License No.'"
                                inputmode="numeric"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                @update:model-value="onEntityOfficerLicenseDigitsChange"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                :model-value="entityOfficerOverlay?.secretaryCertificateNo || ''"
                                :readonly="effectiveFormReadonly"
                                :class="{ 'readonly-field': effectiveFormReadonly }"
                                :label="t('contact.Certificate No.') || 'Certificate No.'"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                @update:model-value="(val: string) => {
                                    if (effectiveFormReadonly) return;
                                    patchEntityOfficerOverlay({ secretaryCertificateNo: String(val ?? '') });
                                }"
                            />
                        </v-col>
                    </template>
                </template>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<style scoped lang="scss">
@import '../scss/contact.scss';

/* 폰트 패밀리 */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contact-basic-section {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

.contact-section-title {
    font-size: 18px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.kyc-status-label {
    font-size: 14px;
    color: rgb(var(--v-theme-on-surface));
}

.kyc-chip {
    background-color: #34d6d2 !important;
    color: #ffffff !important;
    font-weight: 600;
}

.kyc-pill-wrapper {
    align-items: center;
}

.biz-file-field {
    :deep(.v-field) {
        background-color: #f5f5f5 !important;
    }
    :deep(.v-field__input) {
        color: #616161 !important;
    }
}

.identification-no-field {
    :deep(.v-field__input) {
        color: #9e9e9e !important;
    }
}

.required-asterisk {
    color: #F44336 !important;
    font-weight: bold;
    margin-left: 2px;
}

/* Readonly 필드 스타일 (회색 배경) - Street Name과 동일한 스타일 */
.readonly-field {
    opacity: 1 !important;
}

.readonly-field :deep(.v-field) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-input__control) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(input),
.readonly-field :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    cursor: not-allowed !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field--variant-outlined .v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled,
.readonly-field.v-select--disabled,
.readonly-field.v-autocomplete--disabled {
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled :deep(.v-field),
.readonly-field.v-text-field--disabled :deep(.v-field__input),
.readonly-field.v-text-field--disabled :deep(.v-input__control),
.readonly-field.v-text-field--disabled :deep(.v-field__overlay),
.readonly-field.v-text-field--disabled :deep(input),
.readonly-field.v-text-field--disabled :deep(textarea),
.readonly-field.v-select--disabled :deep(.v-field),
.readonly-field.v-select--disabled :deep(.v-field__input),
.readonly-field.v-select--disabled :deep(.v-input__control),
.readonly-field.v-select--disabled :deep(.v-field__overlay),
.readonly-field.v-autocomplete--disabled :deep(.v-field),
.readonly-field.v-autocomplete--disabled :deep(.v-field__input),
.readonly-field.v-autocomplete--disabled :deep(.v-input__control),
.readonly-field.v-autocomplete--disabled :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}

/* Select readonly 스타일 */
.readonly-field.v-select {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field__input) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-input__control) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field__overlay) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

/* Autocomplete readonly 스타일 */
.readonly-field.v-autocomplete {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-autocomplete :deep(.v-field) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-autocomplete :deep(.v-field__input) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-autocomplete :deep(.v-input__control) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-autocomplete :deep(.v-field__overlay) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}
</style>